/**
 * @file Onglet Etoiles : amelioration par emplacement equipe (niveau, chance de reussite, cout en StarGem, garantie progressive).
 */
import { stageValueToDisplay } from '@core/index';
import { formatBig, formatMult, formatPercent } from '@shared/format';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { notify, send } from '../../core/feedback';
import { amountChip } from '../../core/widgets';
import type { UiContext } from '../../UiContext';
import { createTile } from './tile';

interface RowView {
  slot: number;
  root: HTMLElement;
  icon: HTMLElement;
  name: HTMLElement;
  level: HTMLElement;
  stars: HTMLElement;
  chance: HTMLElement;
  mult: HTMLElement;
  btn: HTMLElement;
  /** defId actuellement affiche dans `icon` (reconstruit seulement quand l'arme change). */
  shown: string | null;
}

export class StarsPane extends Component {
  private readonly rows: RowView[] = [];
  private locked!: HTMLElement;
  private list!: HTMLElement;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'stars');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.locked = h('p', { class: 'hint is-hidden' });
    this.list = h('div', { class: 'star-list' });
    const cost = this.ctx.config.balance.stars.gemsPerLevel;
    for (let slot = 0; slot < this.ctx.config.balance.equipSlotUnlocks.length; slot++) {
      const icon = h('div', { class: 'star-icon' });
      const name = h('div', { class: 'star-name' });
      const level = h('div', { class: 'star-level' });
      const stars = h('div', { class: 'star-pips' });
      const chance = h('span', { class: 'star-chance' });
      const mult = h('span', { class: 'star-mult' });
      const btn = h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'star.upgrade', slot }) }, t.t('stars.upgrade', { cost: formatBig(cost) }));
      const root = h(
        'div',
        { class: 'star-row panel' },
        icon,
        h('div', { class: 'star-main' }, h('div', { class: 'star-top' }, h('b', {}, t.t('weapons.slot', { n: slot + 1 })), name), level, stars, h('div', { class: 'star-meta' }, chance, mult)),
        h('div', { class: 'star-buy' }, amountChip('starGem', cost), btn),
      );
      this.rows.push({ slot, root, icon, name, level, stars, chance, mult, btn, shown: null });
      this.list.append(root);
    }
    this.el.append(this.locked, this.list);
    this.listen('StarUpgraded', (e) => notify(t.t(e.success ? 'stars.success' : 'stars.fail'), e.success ? 'success' : 'warning'));
    this.update(0);
  }

  override update(_dt: number): void {
    const { query, data } = this.ctx.sim;
    const { i18n, config } = this.ctx;
    const unlocked = query.feature('stars');
    toggleClass(this.locked, 'is-hidden', unlocked);
    toggleClass(this.list, 'is-hidden', !unlocked);
    if (!unlocked) {
      const rule = config.featureUnlocks.stars;
      setText(this.locked, rule.kind === 'stageValue' ? i18n.t('common.unlockAt', { stage: stageValueToDisplay(config.balance, rule.atLeast).stage }) : i18n.t('common.locked'));
      return;
    }
    const max = config.balance.stars.maxLevel;
    for (const r of this.rows) {
      const uid = data.weapons.equipped[r.slot];
      const item = uid ? data.weapons.items.find((w) => w.uid === uid) : undefined;
      const slotUnlocked = data.progress.bestStageValue >= (config.balance.equipSlotUnlocks[r.slot] ?? Infinity);
      toggleClass(r.root, 'is-hidden', !slotUnlocked);
      if (!slotUnlocked) continue;
      const star = query.star(r.slot);
      if (r.shown !== (item?.defId ?? null)) {
        r.shown = item?.defId ?? null;
        r.icon.replaceChildren();
        if (item) r.icon.append(createTile(this.ctx, this.ctx.index.weapon(item.defId), 'mini').root);
      }
      setText(r.name, item ? i18n.name(this.ctx.index.weapon(item.defId).name) : i18n.t('stars.noWeapon'));
      setText(r.level, i18n.t('stars.level', { level: star.level, max }));
      setText(r.stars, '★'.repeat(star.level) + '☆'.repeat(Math.max(0, max - star.level)));
      setText(r.chance, i18n.t('stars.chance', { chance: formatPercent(star.chance, 0) }));
      setText(r.mult, i18n.t('stars.multiplier', { mult: formatMult(star.multiplier) }));
      const poor = data.wallet.starGem.lt(config.balance.stars.gemsPerLevel);
      toggleClass(r.btn, 'disabled', !item || star.level >= max || poor);
    }
  }
}
